(function () {
  "use strict";

  var DEFAULT_STORAGE_KEY = "inshape-survey-reward-v1";
  var TOKEN_STORAGE_KEY = "inshape-survey-reward-token-v1";
  var REWARD_ENDPOINT = "https://survey.shopinshape.com/api/reward";
  var MAX_REWARD_AMOUNT = 100;
  var MAX_REWARD_LIFETIME = 31 * 24 * 60 * 60 * 1000;
  var CODE_PATTERN = /^SURVEY-[A-Z0-9-]{5,25}$/;
  var popupSelector = [
    '[data-testid="POPUP"]',
    '[role="dialog"][class*="kl-private-reset-css"]',
    '[aria-modal="true"][class*="kl-private-reset-css"]',
    'iframe[src*="klaviyo.com"][title*="popup" i]',
    'iframe[src*="klaviyo.com"][aria-label*="popup" i]'
  ].join(", ");
  var popupObserver = null;
  var popupTimer = null;
  var hiddenPopups = [];
  var marketingSuppressed = false;

  function normalizeCode(value) {
    return String(value || "").trim().toUpperCase();
  }

  function normalizeAmount(value, fallback) {
    var amount = Number(value);
    if (!Number.isFinite(amount)) amount = Number(fallback);
    if (!Number.isFinite(amount) || amount <= 0 || amount > MAX_REWARD_AMOUNT) return null;
    return Math.round(amount * 100) / 100;
  }

  function validate(candidate, fallbackAmount, source) {
    if (!candidate) return { valid: false, status: "missing", reason: "missing" };
    var code = normalizeCode(candidate.discountCode || candidate.code);
    var expiresAt = Date.parse(candidate.expiresAt || "");
    var amount = normalizeAmount(candidate.amount, fallbackAmount);
    var now = Date.now();

    if (String(candidate.status || "active").toLowerCase() !== "active") {
      return { valid: false, status: String(candidate.status || "invalid").toLowerCase(), reason: String(candidate.status || "invalid").toLowerCase() };
    }
    if (!CODE_PATTERN.test(code)) return { valid: false, status: "invalid", reason: "invalid_code" };
    if (!Number.isFinite(expiresAt)) return { valid: false, status: "invalid", reason: "missing_expiry" };
    if (expiresAt <= now) return { valid: false, status: "expired", reason: "expired" };
    if (expiresAt - now > MAX_REWARD_LIFETIME) return { valid: false, status: "invalid", reason: "invalid_expiry" };
    if (amount === null) return { valid: false, status: "invalid", reason: "invalid_amount" };

    return {
      valid: true,
      status: "active",
      state: "valid",
      source: source || candidate.source || "session",
      code: code,
      expiresAt: new Date(expiresAt).toISOString(),
      amount: amount,
      currency: String(candidate.currency || "USD").toUpperCase(),
      minimumSubtotal: Math.max(0, Number(candidate.minimumSubtotal) || amount),
      includesFreeShipping: candidate.includesFreeShipping !== false,
      storedAt: Date.now(),
      version: 2
    };
  }

  function clear(storageKey) {
    var key = storageKey || DEFAULT_STORAGE_KEY;
    try { window.sessionStorage.removeItem(key); } catch (_) {}
    try { window.localStorage.removeItem(key); } catch (_) {}
  }

  function read(storageKey, fallbackAmount) {
    var key = storageKey || DEFAULT_STORAGE_KEY;
    var value = null;
    try { value = JSON.parse(window.sessionStorage.getItem(key) || "null"); } catch (_) {}
    var result = validate(value, fallbackAmount, "session");
    if (!result.valid) clear(key);
    return result;
  }

  function write(storageKey, candidate, fallbackAmount, source) {
    var key = storageKey || DEFAULT_STORAGE_KEY;
    var result = validate(candidate, fallbackAmount, source);
    if (!result.valid) {
      clear(key);
      return result;
    }
    try { window.sessionStorage.setItem(key, JSON.stringify(result)); } catch (_) {}
    try { window.localStorage.removeItem(key); } catch (_) {}
    return result;
  }

  function readToken() {
    try { return window.sessionStorage.getItem(TOKEN_STORAGE_KEY) || ""; } catch (_) { return ""; }
  }

  function clearToken() {
    try { window.sessionStorage.removeItem(TOKEN_STORAGE_KEY); } catch (_) {}
  }

  function resolve(options) {
    var settings = options || {};
    var launch = window.__inshapeSurveyLaunch || {};
    var hasDirectToken = Boolean(launch.hasRewardParam);
    if (launch.hasRewardParam || launch.hasCodeParam || launch.rewardError) {
      // A direct 2+ journey takes precedence over an earlier wear session.
      try {
        window.sessionStorage.removeItem("inshape-wear-reward-v3-token");
        window.sessionStorage.removeItem("inshape-wear-reward-v3-source");
        window.sessionStorage.removeItem("inshape-wear-staff-qa-v1");
      } catch (_) {}
    }
    var rewardError = ["invalid", "expired", "pending", "redeemed", "unavailable"].indexOf(launch.rewardError) !== -1 ? launch.rewardError : "";
    var token = rewardError ? "" : (hasDirectToken ? readToken() : (launch.hasCodeParam ? "" : readToken()));
    var storageKey = settings.storageKey || DEFAULT_STORAGE_KEY;
    var fallbackAmount = settings.defaultAmount || 50;
    try { delete window.__inshapeSurveyLaunch; } catch (_) { window.__inshapeSurveyLaunch = null; }

    if (rewardError) {
      clearToken();
      clear(storageKey);
      return Promise.resolve({ valid: false, status: rewardError, reason: rewardError });
    }

    if (hasDirectToken && !token) {
      clearToken();
      clear(storageKey);
      return Promise.resolve({ valid: false, status: "invalid", reason: "invalid_token" });
    }

    if (launch.hasCodeParam && !hasDirectToken) clearToken();

    if (token) {
      if (token.length < 16 || token.length > 4096 || /\s/.test(token)) {
        clearToken();
        clear(storageKey);
        return Promise.resolve({ valid: false, status: "invalid", reason: "invalid_token" });
      }
      var controller = "AbortController" in window ? new AbortController() : null;
      var timeout = window.setTimeout(function () { if (controller) controller.abort(); }, 7000);
      return fetch(REWARD_ENDPOINT + "?token=" + encodeURIComponent(token), {
        credentials: "omit",
        headers: { Accept: "application/json" },
        referrerPolicy: "no-referrer",
        signal: controller ? controller.signal : undefined
      }).then(function (response) {
        return response.json().catch(function () { return {}; }).then(function (payload) {
          if (!response.ok || !payload.ok || !payload.reward) {
            var status = String(payload.status || payload.error || "unavailable").toLowerCase();
            if (["invalid", "expired", "redeemed"].indexOf(status) !== -1) clearToken();
            clear(storageKey);
            return { valid: false, status: status, reason: status };
          }
          var resolved = write(storageKey, payload.reward, fallbackAmount, "signed_token");
          return resolved;
        });
      }).catch(function () {
        clear(storageKey);
        return { valid: false, status: "unavailable", reason: "unavailable" };
      }).finally(function () {
        window.clearTimeout(timeout);
      });
    }

    if (launch.hasCodeParam) {
      return Promise.resolve(write(storageKey, {
        status: "active",
        discountCode: launch.code,
        expiresAt: launch.expiresAt,
        amount: fallbackAmount,
        currency: settings.currency || "USD",
        minimumSubtotal: fallbackAmount,
        includesFreeShipping: true
      }, fallbackAmount, "legacy_url"));
    }

    return Promise.resolve(read(storageKey, fallbackAmount));
  }

  function hashIdentifier(value) {
    if (!window.crypto || !window.crypto.subtle || !window.TextEncoder) return Promise.resolve(null);
    return window.crypto.subtle.digest("SHA-256", new TextEncoder().encode(value)).then(function (buffer) {
      return Array.prototype.map.call(new Uint8Array(buffer).slice(0, 8), function (byte) {
        return byte.toString(16).padStart(2, "0");
      }).join("");
    }).catch(function () { return null; });
  }

  function track(eventName, details, state) {
    var payload = Object.assign({ event: eventName, survey_reward: true }, details || {});
    var emit = function (rewardId) {
      if (rewardId) payload.reward_id = rewardId;
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push(payload);
    };
    if (state && state.code) return hashIdentifier(state.code).then(emit);
    emit(null);
    return Promise.resolve();
  }

  function scanPopups() {
    popupTimer = null;
    if (!marketingSuppressed) return;
    document.querySelectorAll(popupSelector).forEach(function (popup) {
      if (popup.dataset.ishSurveySuppressed === "true") return;
      popup.dataset.ishSurveySuppressed = "true";
      popup.setAttribute("aria-hidden", "true");
      popup.style.setProperty("display", "none", "important");
      popup.style.setProperty("visibility", "hidden", "important");
      popup.style.setProperty("pointer-events", "none", "important");
      hiddenPopups.push(popup);
    });
    [document.documentElement, document.body].forEach(function (element) {
      if (!element) return;
      element.classList.remove("klaviyo-prevent-body-scrolling");
      if (element.style.overflow === "hidden") element.style.removeProperty("overflow");
    });
  }

  function schedulePopupScan() {
    if (!marketingSuppressed || popupTimer !== null) return;
    popupTimer = window.setTimeout(scanPopups, 50);
  }

  function suppressMarketing(active) {
    if (!document.body) return;
    marketingSuppressed = active;
    document.body.classList.toggle("inshape-no-klaviyo", active);
    if (!active) {
      if (popupTimer !== null) window.clearTimeout(popupTimer);
      popupTimer = null;
      if (popupObserver) popupObserver.disconnect();
      popupObserver = null;
      hiddenPopups.forEach(function (popup) {
        if (!popup.isConnected || popup.dataset.ishSurveySuppressed !== "true") return;
        delete popup.dataset.ishSurveySuppressed;
        popup.removeAttribute("aria-hidden");
        popup.style.removeProperty("display");
        popup.style.removeProperty("visibility");
        popup.style.removeProperty("pointer-events");
      });
      hiddenPopups = [];
      return;
    }
    scanPopups();
    if (!popupObserver) {
      popupObserver = new MutationObserver(schedulePopupScan);
      popupObserver.observe(document.body, { childList: true, subtree: true });
      window.addEventListener("klaviyoForms", schedulePopupScan);
    }
  }

  function applyState(state) {
    var hasReward = Boolean(state && state.valid && state.status === "active");
    document.documentElement.classList.toggle("ish-survey-has-reward", hasReward);
    document.documentElement.classList.toggle("ish-survey-no-reward", !hasReward);
    if (document.body) {
      document.body.classList.toggle("ish-survey-reward-active", hasReward);
      suppressMarketing(hasReward);
    }
    window.dispatchEvent(new CustomEvent("inshape:survey-reward-state", { detail: state }));
  }

  var api = window.InShapeSurveyReward || {
    codePattern: CODE_PATTERN,
    maxAmount: MAX_REWARD_AMOUNT,
    resolve: resolve,
    validate: validate,
    read: read,
    write: write,
    clear: clear,
    track: track,
    applyState: applyState,
    suppressMarketing: suppressMarketing
  };
  window.InShapeSurveyReward = api;

  var runtime = document.querySelector("[data-ish-survey-runtime]");
  if (!runtime) return;

  var storageKey = runtime.dataset.storageKey || DEFAULT_STORAGE_KEY;
  var defaultAmount = normalizeAmount(runtime.dataset.amount, 50) || 50;
  document.documentElement.classList.add("ish-survey-page");

  resolve({ storageKey: storageKey, defaultAmount: defaultAmount }).then(function (state) {
    applyState(state);

    if (!state.valid) {
      runtime.hidden = false;
      var title = runtime.querySelector("[data-ish-recovery-title]");
      var copy = runtime.querySelector("[data-ish-recovery-copy]");
      if (state.status === "expired" || state.status === "redeemed") {
        if (title) title.textContent = state.status === "redeemed" ? "This reward has already been used." : "This reward has expired.";
        if (copy) copy.textContent = "Check your reward email or contact us if you think this is incorrect.";
      } else if (state.status === "pending" || state.status === "unavailable") {
        if (title) title.textContent = "Your reward is still loading.";
        if (copy) copy.textContent = "Refresh this page in a moment. If it still does not load, open the link in your reward email again.";
      }
      var back = runtime.querySelector("[data-ish-recovery-back]");
      if (back) {
        back.addEventListener("click", function () {
          if (window.history.length > 1) window.history.back();
          else window.location.assign("/");
        });
      }
      track("survey_reward_" + state.status, { page_type: "survey_thanks" }, null);
      return;
    }

    runtime.hidden = true;

    function money(value) {
      try {
        return new Intl.NumberFormat(document.documentElement.lang || undefined, {
          style: "currency",
          currency: state.currency,
          minimumFractionDigits: 2
        }).format(value);
      } catch (_) {
        return "$" + value.toFixed(2);
      }
    }

    function parseMoney(value) {
      var text = String(value || "").replace(/[^0-9,.-]/g, "");
      var comma = text.lastIndexOf(",");
      var dot = text.lastIndexOf(".");
      var decimal = Math.max(comma, dot);
      if (decimal >= 0 && text.length - decimal <= 3) {
        text = text.slice(0, decimal).replace(/[.,]/g, "") + "." + text.slice(decimal + 1);
      } else {
        text = text.replace(/[.,]/g, "");
      }
      var parsed = Number(text);
      return Number.isFinite(parsed) ? parsed : 0;
    }

    function discountLink(path) {
      return "/discount/" + encodeURIComponent(state.code) + "?redirect=" + encodeURIComponent(path);
    }

    document.querySelectorAll(".ishl-lprw__code").forEach(function (node) { node.textContent = state.code; });
    document.querySelectorAll(".ishl-lprw__copy").forEach(function (button) {
      button.dataset.code = state.code;
      button.addEventListener("click", function () {
        track("survey_reward_code_copied", { page_type: "survey_thanks" }, state);
      });
    });

    var expiryLabel = new Intl.DateTimeFormat(document.documentElement.lang || undefined, {
      month: "long",
      day: "numeric",
      year: "numeric"
    }).format(new Date(state.expiresAt));
    var fine = document.querySelector(".ishl-lprw__fine");
    if (fine) {
      var dynamicFine = fine.querySelector("[data-ish-survey-dynamic-fine]");
      if (!dynamicFine) {
        dynamicFine = document.createElement("span");
        dynamicFine.dataset.ishSurveyDynamicFine = "true";
        fine.appendChild(document.createTextNode(" "));
        fine.appendChild(dynamicFine);
      }
      dynamicFine.textContent = "Valid through " + expiryLabel + ". Minimum order " + money(state.minimumSubtotal) + ".";
    }

    document.querySelectorAll(".ishl-lpk__card").forEach(function (card, index) {
      var pack = card.querySelector(".ishl-lpk__pack");
      var now = card.querySelector(".ishl-lpk__now");
      var rewardAmount = card.querySelector(".ishl-lpk__reward");
      var tag = card.querySelector(".ishl-lpk__aftertag");
      var note = card.querySelector(".ishl-lpk__note");
      var label = card.querySelector(".ishl-lpk__label");
      var regularPrice = parseMoney(pack && pack.textContent);
      var rewardPrice = Math.max(0, regularPrice - state.amount);
      var packCountMatch = label && label.textContent.match(/(\d+)/);
      var packCount = packCountMatch ? Number(packCountMatch[1]) : 0;

      if (now && regularPrice) now.textContent = money(rewardPrice);
      if (rewardAmount) rewardAmount.textContent = "−" + money(state.amount);
      if (tag) tag.textContent = "Estimated after your " + money(state.amount) + " reward; verified in cart";
      if (note && packCount) note.textContent = money(rewardPrice / packCount) + " a tank" + (state.includesFreeShipping ? ", plus free shipping" : "");

      var targetPath = "";
      try {
        var productUrl = new URL(card.getAttribute("href"), window.location.origin);
        if (productUrl.origin === window.location.origin && productUrl.pathname.indexOf("/products/") === 0) {
          targetPath = productUrl.pathname + productUrl.search + productUrl.hash;
        }
      } catch (_) {}

      card.addEventListener("click", function (event) {
        if (!targetPath) return;
        event.preventDefault();
        var navigated = false;
        var navigate = function () {
          if (navigated) return;
          navigated = true;
          window.location.assign(discountLink(targetPath));
        };
        track("survey_pack_selected", {
          pack_position: index + 1,
          pack_label: label ? label.textContent.trim() : ""
        }, state).finally(navigate);
        window.setTimeout(navigate, 150);
      });
    });

    document.addEventListener("click", function (event) {
      var checkout = event.target.closest('[name="checkout"], a[href*="/checkout"]');
      if (checkout) track("survey_checkout_started", { page_type: "survey_thanks" }, state);
    });

    track("survey_reward_viewed", {
      page_type: "survey_thanks",
      reward_amount: state.amount,
      reward_source: state.source
    }, state);
  });
})();
